import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconBuildingStore,
  IconCalendar,
  IconFileTypeCsv,
  IconPhone,
  IconSearch,
  IconUser,
  IconUserCode,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { repositoryPos } from "../../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { formatDateNotTimeZone } from "../../../../common/FormatDate/FormatDate";
import { BASE_API_POS } from "../../../../config";
import { _localization } from "../../../../config/location";
import { MessageResponse } from "../../../../model/MessageResponse";
import { getMasterOrgSelect } from "../../../../service/getSelectApi";

type CustomerOrderReportItem = {
  maKh: string;
  tenKh: string;
  address: string;
  telephoneNumber: string;
  loaiKh: string;
  maCnMua: string;
  tenCnMua: string;
  slDon: number;
  doanhSo: number;
};

type SearchState = {
  FromDate: string;
  ToDate: string;
  BranchId: string;
  CusType: string;
  MaKh: string;
  TelephoneNumber: string;
};

const CUSTOMER_TYPES = [
  { value: "1", label: "Khách lẻ" },
  { value: "2", label: "Doanh nghiệp" },
  { value: "3", label: "Có hạn mức" },
];

const formatCurrencyVND = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "";
  }

  return Number(value).toLocaleString("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const getDefaultSearch = (storedSearch?: Partial<SearchState> | null) => {
  const currentBranchId = JSON.parse(
    localStorage.getItem("userLoginHeader") || "{}",
  )?.branchId;

  return {
    FromDate:
      storedSearch?.FromDate ||
      new Date(
        new Date(new Date().setMonth(new Date().getMonth() - 1)).getTime() +
          7 * 60 * 60 * 1000,
      ).toISOString(),
    ToDate:
      storedSearch?.ToDate ||
      new Date(new Date().getTime() + 7 * 60 * 60 * 1000).toISOString(),
    BranchId: currentBranchId
      ? String(currentBranchId)
      : storedSearch?.BranchId || "",
    CusType: storedSearch?.CusType || "",
    MaKh: storedSearch?.MaKh || "",
    TelephoneNumber: storedSearch?.TelephoneNumber || "",
  };
};

const CustomerOrderReport = () => {
  const location = useLocation();
  const userLoginHeader = JSON.parse(
    localStorage.getItem("userLoginHeader") || "{}",
  );
  const currentBranchId = userLoginHeader?.branchId
    ? String(userLoginHeader.branchId)
    : "";
  const currentBranchName = userLoginHeader?.brachName || "";
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") === "true";
  const storedSearch = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) || "{}")
    : null;
  const initialSearch = getDefaultSearch(
    storedSearch && checkedPinSearch ? storedSearch : null,
  );

  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<CustomerOrderReportItem[]>([]);
  const [search, setSearch] = useState<SearchState>(initialSearch);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [branchOptions, setBranchOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [visible, { open, close }] = useDisclosure(false);

  const saveSearchState = (nextSearch: SearchState) => {
    sessionStorage.setItem(
      String(location.pathname),
      JSON.stringify(nextSearch),
    );
  };

  const buildQueryString = (includePagination: boolean) => {
    const params = new URLSearchParams();
    const effectiveBranchId = currentBranchId || search.BranchId;

    if (includePagination) {
      params.append("Skip", String(pagination.pageIndex * pagination.pageSize));
      params.append("Take", String(pagination.pageSize));
    }

    if (search.FromDate) {
      params.append("FromDate", formatDateNotTimeZone(search.FromDate));
    }

    if (search.ToDate) {
      params.append("ToDate", formatDateNotTimeZone(search.ToDate));
    }

    if (effectiveBranchId) {
      params.append("BranchId", effectiveBranchId);
    }

    if (search.CusType) {
      params.append("CusType", search.CusType);
    }

    if (search.MaKh.trim()) {
      params.append("MaKh", search.MaKh.trim());
    }

    if (search.TelephoneNumber.trim()) {
      params.append("TelephoneNumber", search.TelephoneNumber.trim());
    }

    return params.toString();
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);

    try {
      const queryString = buildQueryString(true);
      const response = await repositoryPos.get<
        MessageResponse<CustomerOrderReportItem[]>
      >(
        `/api/v1/SaleByCustByOrder/get-customer-order-report-list?${queryString}`,
      );

      if (response?.success) {
        setData(response.data || []);
        setRowCount(response.totalCount || 0);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setRowCount(0);
      console.error("Error fetching customer order report:", error);
    } finally {
      saveSearchState(search);
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const handleSearch = async () => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
      return;
    }

    await fetchData();
  };

  const exportDataExcel = async () => {
    open();

    try {
      const queryString = buildQueryString(false);
      const fullUrl = `${BASE_API_POS}/api/v1/SaleByCustByOrder/export-customer-order-report-excel?${queryString}`;
      const token = localStorage.getItem("token");

      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token?.replace(/"/g, "")}`,
          "Content-Type":
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "bao_cao_so_don_khach_hang.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      NotificationExtension.Success("Export Excel thành công !");
    } catch (error) {
      console.error("Error exporting customer order report:", error);
      NotificationExtension.Fails("Export Excel thất bại !");
    } finally {
      close();
    }
  };

  const getBranchOptions = async () => {
    const response = await getMasterOrgSelect();

    if (response) {
      const filteredBranchOptions = response
        .filter(
          (item) =>
            item.value &&
            item.att1 &&
            item.text &&
            (!currentBranchId || String(item.value) === currentBranchId),
        )
        .map((item) => ({
          value: String(item.value),
          label: `${item.att1} - ${item.text}`,
        }));

      if (
        currentBranchId &&
        currentBranchName &&
        filteredBranchOptions.length === 0
      ) {
        filteredBranchOptions.push({
          value: currentBranchId,
          label: currentBranchName,
        });
      }

      setBranchOptions(filteredBranchOptions);
    }
  };

  const columns = useMemo<MRT_ColumnDef<CustomerOrderReportItem>[]>(
    () => [
      {
        header: "STT",
        accessorKey: "stt",
        Cell: ({ row, table }) => (
          <>
            {row.index +
              1 +
              table.getState().pagination.pageIndex *
                table.getState().pagination.pageSize}
          </>
        ),
        size: 80,
      },
      {
        accessorKey: "maKh",
        header: "Mã khách hàng",
        Cell: ({ renderedCellValue }) => (
          <Badge variant="light" color="blue">
            {renderedCellValue}
          </Badge>
        ),
      },
      {
        accessorKey: "tenKh",
        header: "Tên khách hàng",
        size: 220,
      },
      {
        accessorKey: "address",
        header: "Địa chỉ",
        size: 260,
      },
      {
        accessorKey: "telephoneNumber",
        header: "Số điện thoại",
      },
      {
        accessorKey: "loaiKh",
        header: "Loại khách hàng",
      },
      {
        accessorKey: "maCnMua",
        header: "Mã CN mua",
      },
      {
        accessorKey: "tenCnMua",
        header: "Tên CN mua",
        size: 220,
      },
      {
        accessorKey: "slDon",
        header: "Số lượng đơn",
        Cell: ({ renderedCellValue }) => (
          <Text fw={600}>
            {Number(renderedCellValue || 0).toLocaleString("vi-VN")}
          </Text>
        ),
      },
      {
        accessorKey: "doanhSo",
        header: "Doanh số",
        size: 160,
        Cell: ({ renderedCellValue }) => (
          <Text fw={600}>
            {formatCurrencyVND(Number(renderedCellValue || 0))}
          </Text>
        ),
        Footer: ({ table }) => {
          const total = table
            .getRowModel()
            .rows.reduce((sum, row) => sum + (row.original.doanhSo || 0), 0);

          return (
            <Text ta="right" c="red" fw={700}>
              {formatCurrencyVND(total)}
            </Text>
          );
        },
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data,
    enableTopToolbar: false,
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnActions: true,
    enableColumnFilters: true,
    enableSorting: true,
    enableRowSelection: false,
    manualPagination: true,
    rowCount,
    onPaginationChange: setPagination,
    mantineTableContainerProps: {
      style: {
        maxHeight: height - 100,
        minHeight: height - 100,
      },
    },
    mantineTableBodyCellProps: {
      style: {
        fontWeight: 550,
        fontSize: "12.5px",
        padding: "8px 12px",
      },
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "xl",
      size: "lg",
      rowsPerPageOptions: ["20", "50", "100"],
    },
    localization: _localization,
    initialState: {
      density: "xs",
      columnPinning: {
        left: ["stt", "maKh"],
      },
    },
    state: {
      isLoading,
      pagination,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });

  useEffect(() => {
    void getBranchOptions();
  }, [currentBranchId]);

  useEffect(() => {
    if (currentBranchId && search.BranchId !== currentBranchId) {
      setSearch((prev) => ({
        ...prev,
        BranchId: currentBranchId,
      }));
    }
  }, [currentBranchId, search.BranchId]);

  useEffect(() => {
    void fetchData();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;

    const handleResize = () => {
      setHeight(window.innerHeight - (190 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useHotkeys([
    [
      "Enter",
      () => {
        void handleSearch();
      },
    ],
  ]);

  return (
    <Box pos="relative">
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 4 }}
        loaderProps={{
          children: (
            <Flex direction="column" align="center" gap="xs">
              <Loader type="dots" size={35} />
              <Title size={15}>Đang xử lý...</Title>
            </Flex>
          ),
        }}
      />
      <div ref={headerRef}>
        <Flex
          justify="space-between"
          align="center"
          style={{
            border: "1px solid #dee2e6",
            padding: "5px 10px",
          }}
        >
          <BreadcrumbLayout1 />
          <Button
            leftSection={<IconFileTypeCsv size={16} />}
            onClick={() => exportDataExcel()}
          >
            Export Excel
          </Button>
        </Flex>
        <Grid mt={10}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              leftSection={<IconCalendar color="#15aabf" />}
              value={search.FromDate ? new Date(search.FromDate) : null}
              onChange={(value) =>
                setSearch((prev) => ({
                  ...prev,
                  FromDate: value ? formatDateNotTimeZone(value) : "",
                }))
              }
              maxDate={search.ToDate ? new Date(search.ToDate) : undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              placeholder="Ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              leftSection={<IconCalendar color="#15aabf" />}
              value={search.ToDate ? new Date(search.ToDate) : null}
              onChange={(value) =>
                setSearch((prev) => ({
                  ...prev,
                  ToDate: value ? formatDateNotTimeZone(value) : "",
                }))
              }
              minDate={search.FromDate ? new Date(search.FromDate) : undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Chi nhánh mua"
              data={branchOptions}
              disabled={Boolean(currentBranchId)}
              leftSection={<IconBuildingStore color="#15aabf" />}
              value={currentBranchId || search.BranchId || null}
              onChange={(value) =>
                setSearch((prev) => ({
                  ...prev,
                  BranchId: currentBranchId || value || "",
                }))
              }
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Loại khách hàng"
              clearable
              data={CUSTOMER_TYPES}
              leftSection={<IconUser color="#15aabf" />}
              value={search.CusType || null}
              onChange={(value) =>
                setSearch((prev) => ({
                  ...prev,
                  CusType: value || "",
                }))
              }
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Mã khách hàng"
              leftSection={<IconUserCode color="#15aabf" />}
              value={search.MaKh}
              onChange={(event) =>
                setSearch((prev) => ({
                  ...prev,
                  MaKh: event.currentTarget.value,
                }))
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Số điện thoại"
              leftSection={<IconPhone color="#15aabf" />}
              value={search.TelephoneNumber}
              onChange={(event) =>
                setSearch((prev) => ({
                  ...prev,
                  TelephoneNumber: event.currentTarget.value,
                }))
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={() => {
                void handleSearch();
              }}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default CustomerOrderReport;
